import { Color } from '@tmh-website/shared/colors';
import theme from '@tmh-website/styles/theme';
import transparentize from 'polished/lib/color/transparentize';
import styled, { DefaultTheme, css } from 'styled-components';
import ButtonIcon from '../ButtonIcon';
import { PopupThemeColor, popupThemes } from './Popup.theme';

export const POPUP_Z_INDEX_FADE: number = 2147483631;
export const POPUP_Z_INDEX_MODAL: number = POPUP_Z_INDEX_FADE + 1;
export const POPUP_Z_INDEX_CONTAINER: number = POPUP_Z_INDEX_MODAL + 1;

export const Modal = styled.div<{ $layout: 'regular' | 'fullscreen' | 'fullscreen-centered' }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${POPUP_Z_INDEX_MODAL};
  display: flex;
  align-items: ${props => (props.$layout === 'fullscreen-centered' ? 'center' : 'flex-end')};
  justify-content: center;
  width: 100%;
  height: 100%;
  height: 100dvh;
  @media (${props => props.theme.breakpoint.sm.up}) {
    ${props => props.$layout === 'regular' && 'align-items: center;'};
  }
`;

export const Fade = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: ${POPUP_Z_INDEX_FADE};
  width: 100%;
  height: 300%;
  overflow: hidden;
  touch-action: none;
  background-color: ${props => transparentize(0.3, props.theme.color[Color.Blue_1000_Deep])};
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transition: opacity 0.5s ease;
  @media (${props => props.theme.breakpoint.sm.up}) {
    height: 100%;
  }
`;

interface ContainerProps {
  $themeColor: PopupThemeColor;
  $isVisible: boolean;
  $borderRadius: keyof DefaultTheme['borderRadius'];
  $noPadding?: boolean;
  $layout: 'regular' | 'fullscreen' | 'fullscreen-centered';
  $maxWidth?: string;
  $display: 'flex' | 'block';
  $flexDirection?: 'column' | 'column-reverse';
}

export const POPUP_PADDING_XS = theme.wrapperPaddingMobile;
export const POPUP_PADDING_MD = theme.wrapperPadding;
export const POPUP_PADDING_LG = '40px';

export const Container = styled.div<ContainerProps>`
  z-index: ${POPUP_Z_INDEX_CONTAINER};
  width: 100%;
  max-width: ${props => props.$maxWidth ?? '736px'};
  ${props => !!props.$maxWidth && `flex-basis: ${props.$maxWidth};`};
  height: auto;
  max-height: calc(100% - 60px);
  padding: ${POPUP_PADDING_XS};
  overflow: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  color: ${props => props.theme.color[popupThemes[props.$themeColor].text]};
  background-color: ${props => props.theme.color[popupThemes[props.$themeColor].background]};
  border-radius: ${props =>
    props.$layout === 'fullscreen-centered'
      ? `${props.theme.borderRadius[props.$borderRadius]}`
      : `${props.theme.borderRadius[props.$borderRadius]} ${props.theme.borderRadius[props.$borderRadius]} 0 0`};
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transition: all ${props => props.theme.transitionTiming};
  transition-property: opacity, transform, max-width;
  transform: translateY(${props => (props.$isVisible ? 0 : '16px')});
  ${props => props.$noPadding && 'padding: 0 !important;'};
  @media (${props => props.theme.breakpoint.md.up}) {
    padding: ${POPUP_PADDING_MD};
  }
  @media (${props => props.theme.breakpoint.lg.up}) {
    padding: ${POPUP_PADDING_LG};
  }

  ${props =>
    props.$display === 'flex' &&
    css`
      display: flex;
      flex-direction: ${props.$flexDirection ?? 'column-reverse'};
      @media (${props.theme.breakpoint.sm.up}) {
        flex-direction: column;
      }
    `};

  ${props =>
    props.$layout === 'regular' &&
    css`
      @media (${props => props.theme.breakpoint.sm.up}) {
        border-radius: ${props => props.theme.borderRadius.small};
      }
    `};

  ${props =>
    (props.$layout === 'fullscreen' || props.$layout === 'fullscreen-centered') &&
    css`
      width: 100%;
      max-width: none;
      height: 100%;
      transform: translateY(${props.$isVisible ? 0 : '50%'});
      @media (${props.theme.breakpoint.md.up}) {
        max-width: calc(100vw - 120px);
        max-height: calc(100vh - 80px);
      }
    `};
`;

export const Wrapper = styled.div<{ $themeColorColor: PopupThemeColor }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  color: ${props => props.theme.color[popupThemes[props.$themeColorColor].text]};
  background-color: ${props => props.theme.color[popupThemes[props.$themeColorColor].background]};
`;

export const CloseButton = styled(ButtonIcon)`
  position: absolute;
  top: 20px;
  right: 20px;
`;
